import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL } from 'src/configs/AppConfig';
import { AUTH_TOKEN } from 'src/redux/constants';
import { IErrorResponseData } from 'src/services/Types';

// Define a function type for API calls
type MutationFn<TData, TVariables> = (variables: TVariables) => Promise<TData>;

interface UseAppMutationOptions<TData, TVariables, TError = unknown, TContext = unknown>
  extends UseMutationOptions<TData, TError, TVariables, TContext> {
  baseURL?: string; // Optional custom base URL
  axiosConfig?: AxiosRequestConfig; // Additional Axios options
  method?: 'delete' | 'post' | 'put' | 'patch';
}

const getToken = () => localStorage.getItem(AUTH_TOKEN); // Example: Fetch token from storage

function useAppMutation<TVariables = any, TData = unknown, TError = AxiosError<IErrorResponseData>, TContext = unknown>(
  endpoint: string,
  options: UseAppMutationOptions<TData, TError, TVariables, TContext> = {}
) {
  const mutationFn: MutationFn<TData, TVariables> = async (data) => {
    const { baseURL = API_BASE_URL, axiosConfig, method = 'post' } = options;

    const token = getToken();

    const response: AxiosResponse<TData> = await axios[method](`${baseURL}${endpoint}`, data, {
      ...axiosConfig,
      headers: {
        'Content-Type': 'application/json',
        Authorization: (token ? `Bearer ${token}` : undefined) as any,
        ...axiosConfig?.headers,
      },
    });

    return response.data;
  };

  return useMutation<TData, TError, TVariables, TContext>({ ...options, mutationFn } as UseMutationOptions<TData, TError, TVariables, TContext>);
}

export default useAppMutation;
